import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle, BookOpen, Building, Calendar, Check, ChevronRight,
  Clock, CreditCard, FileText, GraduationCap, IndianRupee,
  Mail, Monitor, Phone, Tag, Upload, User, Users, X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';

import qrCodeImage from '../assets/images/QR code gpay.jpeg';
import logo from '../logo/lo.png';

// ── TYPES ─────────────────────────────────────────────────────────────────────

interface Fees {
  beforeFee: number;
  afterFee: number;
  totalFee: number;
}

interface Instalment {
  date: string;
  type: string;
  amount: number;
  isPartial: boolean;
  timeline: string;
}

interface PaymentHistory {
  beforePlacement: number;
  afterPlacement: number;
  totalPaid: number;
  remainingBalance: number;
  isFullyPaid: boolean;
  hasCompletedSlotSelection: boolean;
  isFirstTimeUser: boolean;
  instalments: Instalment[];
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  collegeName: string;
  degree: string;
  yearOfPassout: string;
  course: string;
  sessionCategory: string;
  paymentScreenshot: File | null;
  paymentType: string;
  paymentAmount: number;
  agreesToTerms: boolean;
  balancePaymentTimeline: string;
  couponCode: string;
  couponApplied: boolean;
  paymentNote: string;
}

interface SlotData {
  sessionType: string;
  selectedSlot: string;
  mode: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  collegeName?: string;
  degree?: string;
  yearOfPassout?: string;
  course?: string;
  sessionCategory?: string;
  agreesToTerms?: string;
  paymentAmount?: string;
  paymentConfirmed?: string;
  paymentScreenshot?: string;
  balancePaymentTimeline?: string;
  [key: string]: string | undefined;
}

interface SubmitData extends FormData {
  [key: string]: unknown;
}

// ── CONSTANTS ─────────────────────────────────────────────────────────────────

const VALID_COUPON = 'VISTA50%';

const COUPON_FEES: Record<string, Fees> = {
  group:        { beforeFee: 8500,  afterFee: 8500,  totalFee: 17000 },
  'one-to-one': { beforeFee: 12500, afterFee: 12500, totalFee: 25000 },
};

const getCourseFees = (_course: string, sessionCategory = '', couponApplied = false): Fees => {
  if (couponApplied) return sessionCategory === 'one-to-one' ? COUPON_FEES['one-to-one'] : COUPON_FEES['group'];
  return sessionCategory === 'one-to-one'
    ? { beforeFee: 15000, afterFee: 15000, totalFee: 30000 }
    : { beforeFee: 10000, afterFee: 10000, totalFee: 20000 };
};

// ── STYLE HELPERS ──────────────────────────────────────────────────────────────

const inp = (err?: string): React.CSSProperties => ({
  width: '100%', padding: '11px 12px 11px 36px', borderRadius: '10px',
  border: `1.5px solid ${err ? '#f87171' : 'rgba(255,255,255,0.1)'}`,
  background: err ? 'rgba(248,113,113,0.08)' : 'rgba(255,255,255,0.05)',
  color: err ? '#fca5a5' : 'white', fontSize: '0.88rem',
  fontFamily: 'Poppins, sans-serif', outline: 'none', boxSizing: 'border-box' as const,
});

const inpNoIcon = (err?: string): React.CSSProperties => ({ ...inp(err), padding: '11px 12px' });

const sel = (err?: string): React.CSSProperties => ({
  ...inp(err), cursor: 'pointer', appearance: 'none' as const,
  background: err ? 'rgba(248,113,113,0.08)' : '#1a1530', paddingRight: '2.5rem',
});

const lbl: React.CSSProperties = {
  display: 'block', color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', fontWeight: '700',
  marginBottom: '7px', textTransform: 'uppercase' as const, letterSpacing: '0.08em',
};

const errTxt: React.CSSProperties = { color: '#f87171', fontSize: '0.72rem', marginTop: '5px', fontWeight: '600' };

const glassCard: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)',
  borderRadius: '22px', padding: '32px', backdropFilter: 'blur(24px)',
};

const innerSection: React.CSSProperties = {
  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '14px', padding: '18px',
};

// ── COMPONENT ─────────────────────────────────────────────────────────────────

const StudentRegistrationForm: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<string>('registration');
  const [isFirstTimeUser, setIsFirstTimeUser] = useState<boolean>(true);
  const [couponInput, setCouponInput] = useState<string>('');
  const [couponStatus, setCouponStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const [formData, setFormData] = useState<FormData>({
    name: '', email: '', phone: '', collegeName: '', degree: '', yearOfPassout: '',
    course: '', sessionCategory: '', paymentScreenshot: null,
    paymentType: 'before_placement', paymentAmount: 0,
    agreesToTerms: false, balancePaymentTimeline: '',
    couponCode: '', couponApplied: false, paymentNote: '',
  });

  const [slotData, setSlotData] = useState<SlotData>({ sessionType: '', selectedSlot: '', mode: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showPayment, setShowPayment] = useState<boolean>(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState<boolean>(false);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory | null>(null);
  const [checkingHistory, setCheckingHistory] = useState<boolean>(false);
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  const [showHistory, setShowHistory] = useState<boolean>(false);

  const fees       = getCourseFees(formData.course, formData.sessionCategory, formData.couponApplied);
  const BEFORE_FEE = fees.beforeFee;
  const AFTER_FEE  = fees.afterFee;
  const TOTAL_FEE  = fees.totalFee;

  const beforePaid      = paymentHistory?.beforePlacement ?? 0;
  const afterPaid       = paymentHistory?.afterPlacement  ?? 0;
  const beforeRemaining = Math.max(0, BEFORE_FEE - beforePaid);
  const afterRemaining  = Math.max(0, AFTER_FEE  - afterPaid);
  const phaseRemaining  = formData.paymentType === 'before_placement' ? beforeRemaining : afterRemaining;
  const totalRemaining  = Math.max(0, TOTAL_FEE - (paymentHistory?.totalPaid ?? 0));
  const balanceAfterThis = Math.max(0, phaseRemaining - (formData.paymentAmount || 0));
  const isPartialPayment = formData.paymentAmount > 0 && formData.paymentAmount < phaseRemaining;

  const handleApplyCoupon = () => {
    if (couponInput.trim().toUpperCase() === VALID_COUPON) {
      setCouponStatus('success');
      setFormData(p => ({ ...p, couponCode: couponInput.trim().toUpperCase(), couponApplied: true, paymentAmount: 0 }));
    } else {
      setCouponStatus('error');
      setFormData(p => ({ ...p, couponCode: '', couponApplied: false }));
    }
  };

  const handleRemoveCoupon = () => {
    setCouponStatus('idle'); setCouponInput('');
    setFormData(p => ({ ...p, couponCode: '', couponApplied: false, paymentAmount: 0 }));
  };

  const courseOptions = [
    { value: '', label: 'Select a course' },
    { value: 'AIML', label: 'AI & ML Development' },
    { value: 'business-analytics', label: 'Business Analytics' },
    { value: 'full-stack-java', label: 'Full Stack Java Development' },
    { value: 'Python full-stack development', label: 'Python Full Stack Development' },
    { value: 'Data Science', label: 'Data Science' },
    { value: 'Data Analytics', label: 'Data Analytics' },
  ];

  const termsAndConditions = [
    'The duration of the course is 3 month validity',
    'Students must complete all weekly assessments. Unexcused absences will be actioned.',
    'Remaining balance is due on the program start date. 48-hour grace period applies.',
    'All official updates via email. Students are responsible for checking regularly.',
    'Students must provide timely updates on daily tasks.',
    'Students experiencing difficulty must notify management within 48 hours.',
    'Internship scheduled based on weekly assessment performance.',
    'Consistent below-expectations performance may extend completion timeframe.',
    'Consistent communication with management is mandatory throughout each project.',
    'Academic dishonesty leads to disciplinary action up to program termination.',
    'Attendance at all scheduled interviews is mandatory.',
    'Sign the daily work-done sheet only upon demonstrating comprehension.',
    'Certification requires completion of all tasks, assessments and projects.',
    `If the student secures a job via Vista Tech, ₹${AFTER_FEE.toLocaleString('en-IN')} must be paid within 10 working days of the offer letter.`,
    'Internship and Live Project based on academic performance.',
    'Minimum 50 marks required in weekly assessments.',
    'Placement support: resume building, mock interviews, referrals, interview scheduling.',
    'Two consecutive days of leave not permitted. Prior notice required.',
    'Once batch timing is finalized, it will never be changed.',
    'Students must settle in before class commencement (online or offline).',
    'Leaving an online class early results in an absence being recorded.',
    `The initial ₹${BEFORE_FEE.toLocaleString('en-IN')} is non-refundable. No refund after training begins.`,
    'Upon fulfilling all terms, the firm offers 100% placement support.',
    "Violation of any term may result in cancellation of refund at company's discretion.",
  ];

  const checkPaymentHistory = async () => {
    if (!formData.email) return;
    setCheckingHistory(true);
    try {
      const url = 'https://script.google.com/macros/s/AKfycbwa2JQXRX1FiiFd6V9dRF3kW2Ph097L55hVvUg_EIJVy6DYlvUb4f_NAXNd9IxnkMgCKA/exec';
      const res = await fetch(
        `${url}?action=checkPaymentHistory&email=${encodeURIComponent(formData.email)}&sessionCategory=${encodeURIComponent(formData.sessionCategory)}&couponApplied=${formData.couponApplied}`,
        { method: 'GET', mode: 'cors' }
      );
      const result = await res.json();
      if (result.status === 'success' && result.paymentHistory) {
        const h: PaymentHistory = result.paymentHistory;
        const isFirst = h.totalPaid === 0;
        setIsFirstTimeUser(isFirst);
        setPaymentHistory({
          ...h,
          remainingBalance: Math.max(0, TOTAL_FEE - (h.totalPaid || 0)),
          isFirstTimeUser: isFirst,
          instalments: h.instalments || [],
        } as PaymentHistory);
        const autoType = h.beforePlacement >= BEFORE_FEE ? 'after_placement' : 'before_placement';
        setFormData(p => ({ ...p, paymentType: autoType, paymentAmount: 0, agreesToTerms: !isFirst }));
      } else {
        setIsFirstTimeUser(true);
        setPaymentHistory({
          beforePlacement: 0, afterPlacement: 0, totalPaid: 0,
          remainingBalance: TOTAL_FEE, isFullyPaid: false,
          hasCompletedSlotSelection: false, isFirstTimeUser: true, instalments: [],
        });
        setFormData(p => ({ ...p, paymentAmount: 0 }));
      }
    } catch (e: unknown) {
      console.error(e);
    } finally {
      setCheckingHistory(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (formData.email && formData.email.includes('@')) checkPaymentHistory(); }, [formData.email]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if ((formData.course || formData.sessionCategory) && formData.email?.includes('@')) checkPaymentHistory(); }, [formData.course, formData.sessionCategory, formData.couponApplied]);

  const validateForm = (): boolean => {
    const e: FormErrors = {};
    if (!formData.name.trim())          e.name = 'Name is required';
    if (!formData.email.trim())         e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = 'Enter a valid email';
    if (!formData.phone.trim())         e.phone = 'Phone is required';
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) e.phone = 'Enter valid 10-digit number';
    if (!formData.collegeName.trim())   e.collegeName = 'College name is required';
    if (!formData.degree.trim())        e.degree = 'Degree is required';
    if (!formData.yearOfPassout.trim()) e.yearOfPassout = 'Year of passout is required';
    else { const y = parseInt(formData.yearOfPassout); if (y < 1950 || y > new Date().getFullYear() + 4) e.yearOfPassout = 'Enter a valid year'; }
    if (!formData.course)          e.course = 'Please select a course';
    if (!formData.sessionCategory) e.sessionCategory = 'Please select a session type';
    if (isFirstTimeUser && !formData.agreesToTerms) e.agreesToTerms = 'You must agree to the terms';
    if (!paymentHistory?.isFullyPaid) {
      if (formData.paymentAmount <= 0) e.paymentAmount = 'Enter the amount you are paying now (min ₹1)';
      if (formData.paymentAmount > phaseRemaining) e.paymentAmount = `Cannot exceed remaining ₹${phaseRemaining.toLocaleString('en-IN')} for this phase`;
      if (!paymentConfirmed) e.paymentConfirmed = 'Please confirm you have completed the payment above';
      if (paymentConfirmed && !formData.paymentScreenshot) e.paymentScreenshot = 'Payment screenshot is required';
      if (isPartialPayment && !formData.balancePaymentTimeline) e.balancePaymentTimeline = 'Please select when you will pay the remaining balance';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleInputChange = (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = ev.target;
    const checked = type === 'checkbox' ? (ev.target as HTMLInputElement).checked : undefined;
    setFormData(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
    if (name === 'paymentAmount') setPaymentConfirmed(false);
  };

  const handlePaymentTypeChange = (type: string) => {
    setFormData(p => ({ ...p, paymentType: type, paymentAmount: 0, balancePaymentTimeline: '' }));
    setPaymentConfirmed(false);
  };

  const handleFileChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0] || null;
    setFormData(p => ({ ...p, paymentScreenshot: file }));
    if (errors.paymentScreenshot) setErrors(p => ({ ...p, paymentScreenshot: '' }));
  };

  const handlePaymentClick = () => {
    const e: FormErrors = {};
    if (!formData.name.trim())        e.name = 'Name is required';
    if (!formData.email.trim())       e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = 'Enter a valid email';
    if (!formData.phone.trim())       e.phone = 'Phone is required';
    if (!formData.collegeName.trim()) e.collegeName = 'College name is required';
    if (!formData.degree.trim())      e.degree = 'Degree is required';
    if (!formData.yearOfPassout.trim()) e.yearOfPassout = 'Year of passout is required';
    if (!formData.course)             e.course = 'Please select a course';
    if (!formData.sessionCategory)    e.sessionCategory = 'Please select a session type';
    if (isFirstTimeUser && !formData.agreesToTerms) e.agreesToTerms = 'You must agree to the terms';
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    if (paymentHistory?.isFullyPaid) { alert('All payments complete!'); return; }
    setShowPayment(true);
  };

  const copyUpiId = () => { navigator.clipboard.writeText('9894449002@okbizaxis'); alert('UPI ID copied!'); };
  const copyText = (text: string, label: string) => { navigator.clipboard.writeText(text); alert(`${label} copied!`); };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((res, rej) => {
      const r = new FileReader();
      r.readAsDataURL(file);
      r.onload = () => {
        const result = r.result;
        if (typeof result === 'string') res(result.split(',')[1]);
        else rej(new Error('FileReader result is not a string'));
      };
      r.onerror = rej;
    });

  const submitToGoogleSheets = async (data: SubmitData) => {
    const url = 'https://script.google.com/macros/s/AKfycbwa2JQXRX1FiiFd6V9dRF3kW2Ph097L55hVvUg_EIJVy6DYlvUb4f_NAXNd9IxnkMgCKA/exec';
    try {
      const base = new URLSearchParams();
      base.append('name', String(data.name));
      base.append('email', String(data.email));
      base.append('phone', String(data.phone));
      base.append('collegeName', String(data.collegeName));
      base.append('degree', String(data.degree));
      base.append('yearOfPassout', String(data.yearOfPassout));
      base.append('course', String(data.course));
      base.append('sessionCategory', String(data.sessionCategory));
      base.append('agreesToTerms', String(data.agreesToTerms));
      base.append('timestamp', new Date().toISOString());
      base.append('isFirstTimeUser', String(isFirstTimeUser));
      base.append('couponCode', String(data.couponCode || ''));
      base.append('couponApplied', String(data.couponApplied));
      base.append('paymentType', String(data.paymentType));
      base.append('paymentAmount', String(data.paymentAmount));
      base.append('isPartialPayment', String(isPartialPayment));
      base.append('phaseTotal', String(data.paymentType === 'before_placement' ? BEFORE_FEE : AFTER_FEE));
      base.append('phaseRemaining', String(phaseRemaining));
      base.append('balanceAfterThis', String(balanceAfterThis));
      base.append('totalCourseRemaining', String(Math.max(0, TOTAL_FEE - (paymentHistory?.totalPaid ?? 0) - Number(data.paymentAmount))));
      base.append('paymentNote', String(data.paymentNote || ''));
      if (data.balancePaymentTimeline) base.append('balancePaymentTimeline', String(data.balancePaymentTimeline));

      let response: Response;
      const screenshot = data.paymentScreenshot as File | null;
      if (screenshot && Number(data.paymentAmount) > 0) {
        const b64 = await fileToBase64(screenshot);
        base.append('action', 'submitPayment');
        base.append('paymentScreenshotBase64', b64);
        base.append('screenshotFilename', screenshot.name);
        base.append('screenshotMimeType', screenshot.type);
        response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: base.toString(), mode: 'cors' });
      } else {
        base.append('action', 'submitRegistration');
        response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: base.toString(), mode: 'cors' });
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const txt = await response.text();
      try {
        const j = JSON.parse(txt);
        return { status: j.status || 'success', message: j.message as string | undefined, success: j.status === 'success', paymentHistory: j.paymentHistory as PaymentHistory | undefined };
      } catch {
        return { status: 'success', success: true };
      }
    } catch (err: unknown) {
      if (err instanceof TypeError && err.message.includes('fetch'))
        return { status: 'error', message: 'Network error.', success: false };
      return { status: 'error', message: err instanceof Error ? err.message : 'Unknown error', success: false };
    }
  };

  const submitSlotRequest = async () => {
    if (!slotData.sessionType || !slotData.mode) { alert('Please select time slot and mode'); return; }
    setIsSubmitting(true);
    try {
      const url = 'https://script.google.com/macros/s/AKfycbwa2JQXRX1FiiFd6V9dRF3kW2Ph097L55hVvUg_EIJVy6DYlvUb4f_NAXNd9IxnkMgCKA/exec';
      const d = new URLSearchParams();
      d.append('action', 'submitSlotRequest'); d.append('name', formData.name); d.append('email', formData.email);
      d.append('phone', formData.phone); d.append('course', formData.course); d.append('sessionCategory', formData.sessionCategory);
      d.append('sessionType', slotData.sessionType); d.append('mode', slotData.mode);
      d.append('status', 'pending'); d.append('timestamp', new Date().toISOString());
      const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: d.toString(), mode: 'cors' });
      if (res.ok) setCurrentStep('completion');
      else throw new Error('Failed');
    } catch { alert('Error. Please try again.'); }
    finally { setIsSubmitting(false); }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const result = await submitToGoogleSheets(formData as unknown as SubmitData);
      if (result.status === 'success' || result.success) {
        if (result.paymentHistory) setPaymentHistory(result.paymentHistory);
        if (isFirstTimeUser) setCurrentStep('slot-selection');
        else setCurrentStep('completion');
      } else { alert('Error: ' + (result.message || 'Please try again.')); }
    } catch { alert('Submission error. Please try again.'); }
    finally { setIsSubmitting(false); }
  };

  const presetAmounts = (() => {
    const presets: number[] = [];
    if (phaseRemaining >= 500)  presets.push(500);
    if (phaseRemaining >= 1000) presets.push(1000);
    if (phaseRemaining >= 2000) presets.push(2000);
    if (phaseRemaining >= 5000) presets.push(5000);
    if (!presets.includes(phaseRemaining) && phaseRemaining > 0) presets.push(phaseRemaining);
    return presets.filter(p => p <= phaseRemaining);
  })();

  // ── SUB-COMPONENTS ───────────────────────────────────────────────────────────

  const Orbs = () => (
    <React.Fragment>{[
      { s: 500, t: '-180px', l: '-150px', c: 'rgba(78,58,105,0.45)' },
      { s: 380, b: '-120px', r: '-80px',  c: 'rgba(240,180,41,0.12)' },
      { s: 260, t: '45%',   l: '58%',    c: 'rgba(255,101,53,0.1)' },
    ].map((o, i) => (
      <motion.div key={i} style={{
        position: 'absolute', width: o.s, height: o.s, borderRadius: '50%',
        background: `radial-gradient(circle, ${o.c}, transparent 70%)`,
        top: o.t, left: o.l, right: o.r, bottom: o.b, pointerEvents: 'none',
      }}
        animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 10 + i * 4, repeat: Infinity, ease: 'easeInOut' }} />
    ))}</React.Fragment>
  );

  const TermsModal = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem', background: 'rgba(13,11,20,0.9)', backdropFilter: 'blur(10px)' }}>
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        style={{ background: '#1a1530', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', maxWidth: '720px', width: '100%', maxHeight: '88vh', overflow: 'hidden', fontFamily: 'Poppins, sans-serif' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', background: 'linear-gradient(135deg,#2b2240,#4e3a69)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FileText size={18} color="white" /></div>
            <div>
              <p style={{ color: 'white', fontWeight: '800', fontSize: '0.95rem', margin: 0 }}>Terms & Conditions</p>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', margin: 0 }}>Vista Tech Pay After Placement Program</p>
            </div>
          </div>
          <button onClick={() => setShowTermsModal(false)} style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={16} color="white" /></button>
        </div>
        <div style={{ padding: '20px 24px', overflowY: 'auto', maxHeight: '62vh' }}>
          {termsAndConditions.map((term, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 14px', borderRadius: '10px', marginBottom: '6px', background: 'rgba(255,255,255,0.02)' }}>
              <span style={{ flexShrink: 0, width: '22px', height: '22px', borderRadius: '6px', background: 'linear-gradient(135deg,#F0B429,#FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.65rem', fontWeight: '900' }}>{i + 1}</span>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem', lineHeight: 1.65, margin: 0 }}>{term}</p>
            </div>
          ))}
          <div style={{ marginTop: '16px', padding: '14px', borderRadius: '12px', background: 'rgba(240,180,41,0.07)', border: '1px solid rgba(240,180,41,0.2)', textAlign: 'center' }}>
            <p style={{ color: '#F0B429', fontSize: '0.78rem', margin: 0 }}>Full terms at <a href="https://vistatech.org/Terms-and-Conditions" target="_blank" rel="noopener noreferrer" style={{ fontWeight: '700', color: '#F0B429' }}>vistatech.org/Terms-and-Conditions</a></p>
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <button onClick={() => setShowTermsModal(false)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', color: '#0d0b14', background: 'linear-gradient(135deg,#F0B429,#FF6535)' }}>I Understand & Close</button>
        </div>
      </motion.div>
    </motion.div>
  );

  const SessionSelectionPage = () => (
    <div style={{ minHeight: '100vh', fontFamily: 'Poppins, sans-serif', background: 'linear-gradient(150deg,#2b2240 0%,#0d0b14 60%,#1a0b24 100%)', position: 'relative', overflow: 'hidden' }}>
      <Orbs />
      <div style={{ position: 'relative', zIndex: 1, padding: '48px 1.5rem 80px' }}>
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <img src={logo} alt="Vista Tech" style={{ height: '44px', objectFit: 'contain', marginBottom: '18px' }} />
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', marginBottom: '16px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
            <span style={{ fontSize: '0.7rem', fontWeight: '700', color: '#4ade80', textTransform: 'uppercase' as const, letterSpacing: '0.1em' }}>Registration Confirmed</span>
          </div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ fontSize: 'clamp(1.5rem,3.5vw,2rem)', fontWeight: '900', color: 'white', marginBottom: '8px' }}>
            Choose Your <span style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Schedule</span>
          </motion.h1>
          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem', margin: 0 }}>For <span style={{ color: 'rgba(255,255,255,0.75)', fontWeight: '600' }}>{formData.course}</span></p>
        </div>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={glassCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '28px', padding: '10px 14px', borderRadius: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
              {formData.sessionCategory === 'one-to-one' ? <User size={16} color="#F0B429" /> : <Users size={16} color="#F0B429" />}
              <span style={{ color: '#F0B429', fontWeight: '700', fontSize: '0.84rem' }}>{formData.sessionCategory === 'one-to-one' ? '1-on-1 Mentorship' : 'Group Session'} — Slot Preferences</span>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Clock size={13} /> Choose Your Time Slot</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px' }}>
                {[
                  { key: 'morning', label: 'Morning', time: '10:00–11:00 AM', emoji: '🌅' },
                  { key: 'afternoon', label: 'Afternoon', time: '2:30–3:30 PM', emoji: '☀️' },
                  { key: 'evening', label: 'Evening', time: '7:30–8:30 PM', emoji: '🌆' },
                ].map(({ key, label, time, emoji }) => {
                  const active = slotData.sessionType === key;
                  return (
                    <motion.button key={key} type="button" whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.97 }}
                      onClick={() => setSlotData(p => ({ ...p, sessionType: key, selectedSlot: '' }))}
                      style={{ padding: '16px 12px', borderRadius: '12px', border: `1.5px solid ${active ? '#F0B429' : 'rgba(255,255,255,0.08)'}`, background: active ? 'rgba(240,180,41,0.1)' : 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left', fontFamily: 'Poppins, sans-serif', position: 'relative' }}>
                      {active && <div style={{ position: 'absolute', top: '8px', right: '8px', width: '18px', height: '18px', borderRadius: '50%', background: 'linear-gradient(135deg,#F0B429,#FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check size={10} color="white" /></div>}
                      <div style={{ fontSize: '1.4rem', marginBottom: '6px' }}>{emoji}</div>
                      <div style={{ fontWeight: '700', fontSize: '0.84rem', color: active ? '#F0B429' : 'rgba(255,255,255,0.75)' }}>{label}</div>
                      <div style={{ fontSize: '0.72rem', color: active ? '#F0B429' : 'rgba(255,255,255,0.4)', marginTop: '2px' }}>{time}</div>
                      <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', marginTop: '1px' }}>Weekdays</div>
                    </motion.button>
                  );
                })}
              </div>
            </div>
            {slotData.sessionType && (
              <div style={{ marginBottom: '24px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Monitor size={13} /> Learning Mode</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {[
                    { key: 'online', icon: <Monitor size={18} />, label: 'Online', sub: 'Join from anywhere', features: ['Zoom / Google Meet', 'Interactive sessions', 'Recorded for review'], col: '#3b82f6' },
                    { key: 'offline', icon: <Users size={18} />, label: 'In-Person', sub: 'At our training center', features: ['Face-to-face learning', 'Hands-on experience', 'Direct mentor access'], col: '#10b981' },
                  ].map(({ key, icon, label, sub, features, col }) => {
                    const active = slotData.mode === key;
                    return (
                      <motion.button key={key} type="button" whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
                        onClick={() => setSlotData(p => ({ ...p, mode: key }))}
                        style={{ padding: '18px', borderRadius: '12px', border: `1.5px solid ${active ? col : 'rgba(255,255,255,0.08)'}`, background: active ? `${col}15` : 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left', fontFamily: 'Poppins, sans-serif', position: 'relative' }}>
                        {active && <div style={{ position: 'absolute', top: '10px', right: '10px', width: '18px', height: '18px', borderRadius: '50%', background: col, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check size={10} color="white" /></div>}
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: active ? col : 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', color: active ? 'white' : 'rgba(255,255,255,0.35)' }}>{icon}</div>
                        <div style={{ fontWeight: '700', fontSize: '0.88rem', color: active ? col : 'rgba(255,255,255,0.75)' }}>{label}</div>
                        <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginBottom: '10px' }}>{sub}</div>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {features.map(f => <li key={f} style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ width: '4px', height: '4px', borderRadius: '50%', background: active ? col : 'rgba(255,255,255,0.2)', flexShrink: 0 }} />{f}</li>)}
                        </ul>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            )}
            {slotData.sessionType && slotData.mode && (
              <div style={{ ...innerSection, marginBottom: '24px' }}>
                <p style={{ ...lbl, marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}><Check size={12} /> Confirmation Summary</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {([
                    ['Name', formData.name], ['Course', formData.course], ['Phone', formData.phone],
                    ['Session', formData.sessionCategory === 'one-to-one' ? '1-on-1' : 'Group'],
                    ['Time', slotData.sessionType === 'morning' ? 'Morning (10–11 AM)' : slotData.sessionType === 'afternoon' ? 'Afternoon (2:30–3:30 PM)' : 'Evening (7:30–8:30 PM)'],
                    ['Mode', slotData.mode.charAt(0).toUpperCase() + slotData.mode.slice(1)],
                  ] as [string, string][]).map(([k, v]) => (
                    <div key={k} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px' }}>
                      <p style={{ ...lbl, marginBottom: '2px' }}>{k}</p>
                      <p style={{ color: 'white', fontSize: '0.84rem', fontWeight: '600', margin: 0 }}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <motion.button onClick={submitSlotRequest} disabled={!slotData.sessionType || !slotData.mode || isSubmitting}
              whileHover={!slotData.sessionType || !slotData.mode || isSubmitting ? {} : { scale: 1.01 }}
              whileTap={!slotData.sessionType || !slotData.mode || isSubmitting ? {} : { scale: 0.98 }}
              style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', cursor: !slotData.sessionType || !slotData.mode || isSubmitting ? 'not-allowed' : 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.95rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: !slotData.sessionType || !slotData.mode || isSubmitting ? 'rgba(255,255,255,0.07)' : 'linear-gradient(135deg,#F0B429,#FF6535)', color: !slotData.sessionType || !slotData.mode || isSubmitting ? 'rgba(255,255,255,0.25)' : '#0d0b14' }}>
              {isSubmitting
                ? <><span style={{ width: '18px', height: '18px', border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />Confirming...</>
                : <><Check size={18} />Confirm Session Preference</>}
            </motion.button>
            <p style={{ textAlign: 'center', fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)', marginTop: '12px' }}>Reviewed within 24–48 hours · Confirmation sent to your email</p>
          </motion.div>
        </div>
      </div>
    </div>
  );

  const CompletionPage = () => (
    <div style={{ minHeight: '100vh', fontFamily: 'Poppins, sans-serif', background: 'linear-gradient(150deg,#2b2240 0%,#0d0b14 60%,#1a0b24 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 1.5rem', position: 'relative', overflow: 'hidden' }}>
      <Orbs />
      <div style={{ position: 'relative', zIndex: 1, maxWidth: '440px', width: '100%' }}>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', overflow: 'hidden', backdropFilter: 'blur(24px)' }}>
          <div style={{ padding: '40px 32px', textAlign: 'center', background: isFirstTimeUser ? 'linear-gradient(135deg,#2b2240,#4e3a69)' : 'linear-gradient(135deg,#065f46,#059669)' }}>
            <motion.div animate={{ rotate: [0, -8, 8, -8, 0] }} transition={{ delay: 0.3, duration: 0.7 }}
              style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'linear-gradient(135deg,#F0B429,#FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', boxShadow: '0 0 48px rgba(240,180,41,0.5)' }}>
              <Check size={34} color="white" />
            </motion.div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: '900', color: 'white', marginBottom: '6px' }}>{isFirstTimeUser ? 'Request Submitted!' : 'Payment Recorded!'}</h1>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.84rem', margin: 0 }}>{isPartialPayment ? 'Partial payment saved — balance tracked' : 'Payment complete for this phase'}</p>
          </div>
          <div style={{ padding: '28px' }}>
            <div style={{ padding: '16px', borderRadius: '14px', background: 'rgba(240,180,41,0.07)', border: '1px solid rgba(240,180,41,0.2)', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isPartialPayment ? '10px' : '0' }}>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>Paid Now</span>
                <span style={{ color: '#F0B429', fontSize: '1.3rem', fontWeight: '900' }}>₹{formData.paymentAmount.toLocaleString('en-IN')}</span>
              </div>
              {isPartialPayment && <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                  <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem' }}>Balance this phase</span>
                  <span style={{ color: '#f87171', fontSize: '0.9rem', fontWeight: '700' }}>₹{balanceAfterThis.toLocaleString('en-IN')}</span>
                </div>
                {formData.balancePaymentTimeline && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px' }}>
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem' }}>Committed by</span>
                    <span style={{ color: '#fbbf24', fontSize: '0.82rem', fontWeight: '700' }}>{formData.balancePaymentTimeline}</span>
                  </div>
                )}
              </>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
              {([
                ['Name', formData.name], ['Course', formData.course],
                ['Phase', formData.paymentType === 'before_placement' ? 'Before Placement' : 'After Placement'],
                ['Status', isPartialPayment ? '⏳ Partial' : '✓ Phase Done'],
              ] as [string, string][]).map(([k, v]) => (
                <div key={k} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px' }}>
                  <p style={{ ...lbl, marginBottom: '2px' }}>{k}</p>
                  <p style={{ color: k === 'Status' ? (isPartialPayment ? '#fbbf24' : '#34d399') : 'white', fontSize: '0.82rem', fontWeight: '600', margin: 0 }}>{v}</p>
                </div>
              ))}
            </div>
            {isFirstTimeUser && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                {['Admin reviews your session within 24–48 hrs', 'Confirmation email with joining details sent', 'Class starts as per confirmed schedule'].map((step, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 12px', borderRadius: '10px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ width: '20px', height: '20px', borderRadius: '6px', background: 'linear-gradient(135deg,#F0B429,#FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', fontSize: '0.6rem', fontWeight: '900' }}>{i + 1}</div>
                    <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.78rem', lineHeight: 1.55, margin: 0 }}>{step}</p>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', borderRadius: '10px', background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)' }}>
              <span style={{ fontSize: '1.1rem' }}>📬</span>
              <p style={{ color: '#fbbf24', fontSize: '0.78rem', lineHeight: 1.55, margin: 0 }}>Check inbox & spam for updates from Vista Tech. Every instalment is tracked and recorded.</p>
            </div>
            <p style={{ textAlign: 'center', fontSize: '0.7rem', color: 'rgba(255,255,255,0.25)', marginTop: '16px' }}>Thank you for choosing Vista Tech · support@vistatech.com</p>
          </div>
        </motion.div>
      </div>
    </div>
  );

  if (currentStep === 'slot-selection') return <SessionSelectionPage />;
  if (currentStep === 'completion')     return <CompletionPage />;

  const origFees = getCourseFees(formData.course, formData.sessionCategory, false);

  // ── MAIN RENDER ──────────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: '100vh', fontFamily: 'Poppins, sans-serif', background: 'linear-gradient(150deg,#2b2240 0%,#0d0b14 60%,#1a0b24 100%)', position: 'relative', overflow: 'hidden' }}>
      <Orbs />
      <div style={{ position: 'relative', zIndex: 1, padding: '48px 1.5rem 80px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <img src={logo} alt="Vista Tech" style={{ height: '44px', objectFit: 'contain', marginBottom: '18px' }} />
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', background: 'rgba(240,180,41,0.1)', border: '1px solid rgba(240,180,41,0.25)', marginBottom: '16px' }}>
            <motion.span animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity }} style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#F0B429', display: 'inline-block' }} />
            <span style={{ fontSize: '0.68rem', fontWeight: '800', color: '#F0B429', textTransform: 'uppercase' as const, letterSpacing: '0.1em' }}>Vista Tech — Official Enrollment Portal</span>
          </div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ fontSize: 'clamp(2rem,5vw,3rem)', fontWeight: '900', color: 'white', lineHeight: 1.1, marginBottom: '10px' }}>
            50% <span style={{ background: 'linear-gradient(135deg,#F0B429,#FF6535)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Pay After</span> Placement
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.9rem' }}>
            Launch your tech career — pay in instalments, at your pace
          </motion.p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px', marginTop: '16px' }}>
            {[['🔒', 'SSL Secure'], ['✓', '100% Verified'], ['💳', 'Flexible Payments']].map(([icon, label]) => (
              <span key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}><span>{icon}</span><span>{label}</span></span>
            ))}
          </div>
        </div>

        <div style={{ maxWidth: '580px', margin: '0 auto' }}>
          <AnimatePresence>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} style={glassCard}>

              {/* Returning student banner */}
              {paymentHistory && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '10px', marginBottom: '24px', background: isFirstTimeUser ? 'rgba(240,180,41,0.1)' : 'rgba(245,158,11,0.1)', border: `1px solid ${isFirstTimeUser ? 'rgba(240,180,41,0.25)' : 'rgba(245,158,11,0.25)'}` }}>
                  <motion.span animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 2, repeat: Infinity }} style={{ width: '8px', height: '8px', borderRadius: '50%', background: isFirstTimeUser ? '#F0B429' : '#f59e0b', flexShrink: 0, display: 'inline-block' }} />
                  <span style={{ fontSize: '0.82rem', fontWeight: '700', color: isFirstTimeUser ? '#F0B429' : '#fbbf24' }}>
                    {isFirstTimeUser ? '🆕 New Registration' : '🔄 Returning Student — Continue Payments'}
                  </span>
                </div>
              )}

              {/* Course select */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><GraduationCap size={13} /> Select Your Course</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)', pointerEvents: 'none', zIndex: 1 }}><GraduationCap size={15} /></span>
                  <select name="course" value={formData.course} onChange={handleInputChange} style={sel(errors.course)}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }} onBlur={e => { e.target.style.borderColor = errors.course ? '#f87171' : 'rgba(255,255,255,0.1)'; }}>
                    {courseOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <span style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)', pointerEvents: 'none', fontSize: '0.7rem' }}>▾</span>
                </div>
                {errors.course && <p style={errTxt}>⚠ {errors.course}</p>}
              </div>

              {/* Session type */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Users size={13} /> Session Type</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {[
                    { key: 'group', icon: <Users size={20} />, label: 'Group Session', sub: 'Learn collaboratively', col: '#F0B429', grad: 'linear-gradient(135deg,#F0B429,#FF6535)' },
                    { key: 'one-to-one', icon: <User size={20} />, label: '1-on-1 Mentorship', sub: 'Dedicated personal guidance', col: '#a78bfa', grad: 'linear-gradient(135deg,#4e3a69,#2b2240)' },
                  ].map(({ key, icon, label: btnLabel, sub, col, grad }) => {
                    const active = formData.sessionCategory === key;
                    return (
                      <motion.button key={key} type="button" whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.97 }}
                        onClick={() => { setFormData(p => ({ ...p, sessionCategory: key, paymentAmount: 0 })); setPaymentConfirmed(false); setShowPayment(false); }}
                        style={{ padding: '16px', borderRadius: '12px', border: `1.5px solid ${active ? col : 'rgba(255,255,255,0.08)'}`, background: active ? `${col}12` : 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left', fontFamily: 'Poppins, sans-serif', position: 'relative' }}>
                        {active && <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} style={{ position: 'absolute', top: '10px', right: '10px', width: '18px', height: '18px', borderRadius: '50%', background: grad, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check size={10} color="white" /></motion.div>}
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: active ? grad : 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', color: active ? 'white' : 'rgba(255,255,255,0.3)' }}>{icon}</div>
                        <div style={{ fontWeight: '700', fontSize: '0.88rem', color: active ? col : 'rgba(255,255,255,0.7)' }}>{btnLabel}</div>
                        <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>{sub}</div>
                      </motion.button>
                    );
                  })}
                </div>
                {errors.sessionCategory && <p style={errTxt}>⚠ {errors.sessionCategory}</p>}
              </div>

              {/* Coupon */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Tag size={13} /> Coupon Code <span style={{ color: 'rgba(255,255,255,0.25)', fontWeight: 400, textTransform: 'none' as const, letterSpacing: 0 }}>(optional)</span>
                </label>
                {!formData.couponApplied ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: couponStatus === 'error' ? '#f87171' : 'rgba(255,255,255,0.35)', pointerEvents: 'none' }}><Tag size={15} /></span>
                      <input type="text" value={couponInput}
                        onChange={e => { setCouponInput(e.target.value); setCouponStatus('idle'); }}
                        onKeyDown={e => { if (e.key === 'Enter') handleApplyCoupon(); }}
                        placeholder="Enter coupon code"
                        style={{ ...inp(couponStatus === 'error' ? 'err' : undefined), textTransform: 'uppercase' as const, letterSpacing: '0.08em' }}
                        onFocus={e => { e.target.style.borderColor = '#F0B429'; }}
                        onBlur={e => { e.target.style.borderColor = couponStatus === 'error' ? '#f87171' : 'rgba(255,255,255,0.1)'; }} />
                    </div>
                    <motion.button type="button" onClick={handleApplyCoupon} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                      style={{ padding: '11px 20px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.84rem', background: 'linear-gradient(135deg,#F0B429,#FF6535)', color: '#0d0b14', whiteSpace: 'nowrap' }}>Apply</motion.button>
                  </div>
                ) : (
                  <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderRadius: '10px', background: 'rgba(52,211,153,0.08)', border: '1.5px solid rgba(52,211,153,0.35)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'linear-gradient(135deg,#10b981,#059669)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Tag size={14} color="white" /></div>
                      <div>
                        <p style={{ color: '#34d399', fontWeight: '800', fontSize: '0.84rem', margin: 0 }}>{formData.couponCode} — Applied!</p>
                        <p style={{ color: 'rgba(52,211,153,0.65)', fontSize: '0.72rem', margin: 0 }}>Save ₹{(origFees.totalFee - fees.totalFee).toLocaleString('en-IN')} on total fees</p>
                      </div>
                    </div>
                    <button type="button" onClick={handleRemoveCoupon} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: '4px' }}><X size={16} /></button>
                  </motion.div>
                )}
                {couponStatus === 'error' && <p style={errTxt}>⚠ Invalid coupon code. Please try again.</p>}
                {formData.couponApplied && formData.sessionCategory && (
                  <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
                    style={{ marginTop: '10px', padding: '14px 16px', borderRadius: '12px', background: 'rgba(52,211,153,0.05)', border: '1px solid rgba(52,211,153,0.2)' }}>
                    <p style={{ ...lbl, color: '#34d399', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}><Check size={12} /> Discounted Fee Breakdown</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                      {([
                        { label: 'Before', orig: origFees.beforeFee, disc: fees.beforeFee },
                        { label: 'After', orig: origFees.afterFee, disc: fees.afterFee },
                        { label: 'Total', orig: origFees.totalFee, disc: fees.totalFee },
                      ]).map(({ label: fl, orig, disc }) => (
                        <div key={fl} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px', textAlign: 'center' }}>
                          <p style={{ ...lbl, marginBottom: '4px', fontSize: '0.62rem' }}>{fl} Placement</p>
                          <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem', margin: '0 0 2px', textDecoration: 'line-through' }}>₹{orig.toLocaleString('en-IN')}</p>
                          <p style={{ color: '#34d399', fontSize: '0.92rem', fontWeight: '900', margin: 0 }}>₹{disc.toLocaleString('en-IN')}</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Email */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Mail size={13} /> Email Address</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><Mail size={15} /></span>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="you@example.com" style={inp(errors.email)}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }} onBlur={e => { e.target.style.borderColor = errors.email ? '#f87171' : 'rgba(255,255,255,0.1)'; }} />
                </div>
                {errors.email && <p style={errTxt}>⚠ {errors.email}</p>}
                {checkingHistory && <p style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '0.75rem', color: '#F0B429', marginTop: '6px', fontWeight: '600' }}><span style={{ width: '12px', height: '12px', border: '2px solid #F0B429', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Checking your payment history...</p>}
              </div>

              {/* Payment History Panel */}
              {paymentHistory && (
                <div style={{ ...innerSection, marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <CreditCard size={16} color="rgba(255,255,255,0.5)" />
                      <span style={{ color: 'white', fontWeight: '700', fontSize: '0.84rem' }}>Payment Summary</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {paymentHistory.isFullyPaid && <span style={{ fontSize: '0.65rem', fontWeight: '800', padding: '3px 10px', borderRadius: '999px', background: 'rgba(52,211,153,0.15)', color: '#6ee7b7', textTransform: 'uppercase' as const, letterSpacing: '0.06em' }}>✓ Fully Paid</span>}
                      {(paymentHistory.instalments || []).length > 0 && (
                        <button type="button" onClick={() => setShowHistory(h => !h)} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', cursor: 'pointer', color: '#F0B429', fontSize: '0.72rem', fontWeight: '700', fontFamily: 'Poppins, sans-serif' }}>
                          {showHistory ? 'Hide' : 'View'} history <ChevronRight size={12} style={{ transform: showHistory ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                    {([
                      { label: 'Before Placement Paid', val: beforePaid, color: '#34d399' },
                      { label: 'After Placement Paid', val: afterPaid, color: '#34d399' },
                      { label: 'Total Paid', val: paymentHistory.totalPaid, color: '#a78bfa' },
                      { label: 'Total Remaining', val: totalRemaining, color: totalRemaining > 0 ? '#f87171' : '#34d399' },
                    ] as { label: string; val: number; color: string }[]).map(({ label: hl, val, color }) => (
                      <div key={hl} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px 12px' }}>
                        <p style={{ ...lbl, marginBottom: '3px' }}>{hl}</p>
                        <p style={{ color, fontSize: '1rem', fontWeight: '900', margin: 0 }}>₹{val.toLocaleString('en-IN')}</p>
                      </div>
                    ))}
                  </div>
                  {formData.sessionCategory && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {[
                        { label: 'Before Placement', paid: beforePaid, total: BEFORE_FEE, color: '#F0B429' },
                        { label: 'After Placement', paid: afterPaid, total: AFTER_FEE, color: '#a78bfa' },
                      ].map(({ label: bl, paid, total, color }) => {
                        const pct = Math.min(100, total > 0 ? (paid / total) * 100 : 0);
                        return (
                          <div key={bl}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>{bl}</span>
                              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', fontWeight: '700' }}>₹{paid.toLocaleString('en-IN')} / ₹{total.toLocaleString('en-IN')}</span>
                            </div>
                            <div style={{ height: '6px', borderRadius: '999px', background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                              <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, ease: 'easeOut' }} style={{ height: '100%', borderRadius: '999px', background: pct >= 100 ? '#34d399' : color }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <AnimatePresence>
                    {showHistory && (paymentHistory.instalments || []).length > 0 && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                        <p style={{ ...lbl, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}><Clock size={12} /> Payment History</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {paymentHistory.instalments.map((inst: Instalment, i: number) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: inst.type === 'before_placement' ? '#F0B429' : '#a78bfa', flexShrink: 0 }} />
                                <div>
                                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.78rem', fontWeight: '600', margin: 0 }}>{inst.type === 'before_placement' ? 'Before' : 'After'} Placement {inst.isPartial ? '(Partial)' : ''}</p>
                                  <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem', margin: 0 }}>{inst.date}</p>
                                </div>
                              </div>
                              <span style={{ color: inst.isPartial ? '#fbbf24' : '#34d399', fontWeight: '800', fontSize: '0.88rem' }}>₹{inst.amount.toLocaleString('en-IN')}</span>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Divider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0' }}>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                <span style={lbl}>Personal Details</span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
              </div>

              {/* Name */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><User size={13} /> Full Name</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><User size={15} /></span>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter your full name" style={inp(errors.name)}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }} onBlur={e => { e.target.style.borderColor = errors.name ? '#f87171' : 'rgba(255,255,255,0.1)'; }} />
                </div>
                {errors.name && <p style={errTxt}>⚠ {errors.name}</p>}
              </div>

              {/* Phone */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Phone size={13} /> Phone Number</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><Phone size={15} /></span>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="10-digit mobile number" style={inp(errors.phone)}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }} onBlur={e => { e.target.style.borderColor = errors.phone ? '#f87171' : 'rgba(255,255,255,0.1)'; }} />
                </div>
                {errors.phone && <p style={errTxt}>⚠ {errors.phone}</p>}
              </div>

              {/* College + Degree */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Building size={13} /> College</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><Building size={15} /></span>
                    <input type="text" name="collegeName" value={formData.collegeName} onChange={handleInputChange} placeholder="College name" style={inp(errors.collegeName)}
                      onFocus={e => { e.target.style.borderColor = '#F0B429'; }} onBlur={e => { e.target.style.borderColor = errors.collegeName ? '#f87171' : 'rgba(255,255,255,0.1)'; }} />
                  </div>
                  {errors.collegeName && <p style={errTxt}>⚠ {errors.collegeName}</p>}
                </div>
                <div>
                  <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><BookOpen size={13} /> Degree</label>
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><BookOpen size={15} /></span>
                    <input type="text" name="degree" value={formData.degree} onChange={handleInputChange} placeholder="e.g. B.Tech, BCA" style={inp(errors.degree)}
                      onFocus={e => { e.target.style.borderColor = '#F0B429'; }} onBlur={e => { e.target.style.borderColor = errors.degree ? '#f87171' : 'rgba(255,255,255,0.1)'; }} />
                  </div>
                  {errors.degree && <p style={errTxt}>⚠ {errors.degree}</p>}
                </div>
              </div>

              {/* Year */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Calendar size={13} /> Year of Passout</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)' }}><Calendar size={15} /></span>
                  <input type="number" name="yearOfPassout" value={formData.yearOfPassout} onChange={handleInputChange} placeholder="e.g. 2025" min="1950" max="2030" style={inp(errors.yearOfPassout)}
                    onFocus={e => { e.target.style.borderColor = '#F0B429'; }} onBlur={e => { e.target.style.borderColor = errors.yearOfPassout ? '#f87171' : 'rgba(255,255,255,0.1)'; }} />
                </div>
                {errors.yearOfPassout && <p style={errTxt}>⚠ {errors.yearOfPassout}</p>}
              </div>

              {/* Terms */}
              {isFirstTimeUser && (
                <div style={{ padding: '16px', borderRadius: '12px', background: errors.agreesToTerms ? 'rgba(248,113,113,0.08)' : 'rgba(255,255,255,0.04)', border: `1.5px solid ${errors.agreesToTerms ? '#f87171' : 'rgba(255,255,255,0.1)'}`, marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <input type="checkbox" id="agreesToTerms" name="agreesToTerms" checked={formData.agreesToTerms} onChange={handleInputChange}
                      style={{ width: '18px', height: '18px', marginTop: '2px', cursor: 'pointer', accentColor: '#F0B429', flexShrink: 0 }} />
                    <label htmlFor="agreesToTerms" style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.84rem', lineHeight: 1.55, cursor: 'pointer' }}>
                      I agree to the{' '}
                      <button type="button" onClick={() => setShowTermsModal(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F0B429', fontWeight: '700', fontSize: '0.84rem', fontFamily: 'Poppins, sans-serif', textDecoration: 'underline', padding: 0 }}>Terms & Conditions</button>
                      {' '}of Vista Tech's Pay After Placement program.
                      {formData.agreesToTerms && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px', color: '#34d399', fontWeight: '700', fontSize: '0.75rem' }}><Check size={12} /> Accepted</span>}
                    </label>
                  </div>
                  {errors.agreesToTerms && <p style={errTxt}>⚠ {errors.agreesToTerms}</p>}
                </div>
              )}

              {!isFirstTimeUser && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px 16px', borderRadius: '12px', background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)', marginBottom: '20px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'linear-gradient(135deg,#F0B429,#FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Check size={16} color="white" /></div>
                  <div>
                    <p style={{ color: '#fbbf24', fontWeight: '800', fontSize: '0.88rem', margin: '0 0 3px' }}>Welcome Back!</p>
                    <p style={{ color: 'rgba(251,191,36,0.7)', fontSize: '0.78rem', margin: 0 }}>Terms already accepted. Submit your next instalment below.</p>
                  </div>
                </div>
              )}

              {/* Proceed to payment */}
              {!paymentHistory?.isFullyPaid && !showPayment && (
                <motion.button type="button" onClick={handlePaymentClick} whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}
                  style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: 'linear-gradient(135deg,#F0B429,#FF6535)', color: '#0d0b14', boxShadow: '0 8px 28px rgba(240,180,41,0.35)' }}>
                  <CreditCard size={20} />
                  {isFirstTimeUser ? 'Proceed to Payment →' : 'Submit Next Instalment →'}
                </motion.button>
              )}

              {paymentHistory?.isFullyPaid && (
                <div style={{ padding: '18px', borderRadius: '12px', background: 'rgba(52,211,153,0.08)', border: '1.5px solid rgba(52,211,153,0.3)', textAlign: 'center' }}>
                  <Check size={28} color="#34d399" style={{ margin: '0 auto 8px', display: 'block' }} />
                  <p style={{ color: '#34d399', fontWeight: '800', fontSize: '0.95rem', margin: '0 0 4px' }}>All Payments Complete!</p>
                  <p style={{ color: 'rgba(52,211,153,0.65)', fontSize: '0.8rem', margin: 0 }}>Your course fees are fully settled.</p>
                </div>
              )}

              {/* PAYMENT PANEL */}
              {showPayment && !paymentHistory?.isFullyPaid && (
                <div style={{ ...innerSection, marginTop: '4px' }}>

                  {/* Panel header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', padding: '14px 16px', borderRadius: '10px', background: 'linear-gradient(135deg,#2b2240,#4e3a69)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IndianRupee size={17} color="white" /></div>
                      <div>
                        <p style={{ color: 'white', fontWeight: '700', fontSize: '0.88rem', margin: 0 }}>{isFirstTimeUser ? 'Registration Payment' : 'Instalment Payment'}</p>
                        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem', margin: 0 }}>Secure UPI · Pay any amount you can now</p>
                      </div>
                    </div>
                    {formData.couponApplied && <span style={{ fontSize: '0.65rem', fontWeight: '800', padding: '3px 10px', borderRadius: '999px', background: 'rgba(52,211,153,0.15)', color: '#6ee7b7', textTransform: 'uppercase' as const, letterSpacing: '0.06em' }}>Coupon ✓</span>}
                  </div>

                  {/* Phase selector for returning students */}
                  {!isFirstTimeUser && (
                    <div style={{ marginBottom: '18px' }}>
                      <label style={lbl}>Which Phase Are You Paying?</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        {[
                          { key: 'before_placement', label: 'Before Placement', total: BEFORE_FEE, paid: beforePaid, remaining: beforeRemaining, color: '#F0B429' },
                          { key: 'after_placement', label: 'After Placement', total: AFTER_FEE, paid: afterPaid, remaining: afterRemaining, color: '#a78bfa' },
                        ].map(({ key, label: pl, total, paid, remaining, color }) => {
                          const active = formData.paymentType === key;
                          const done = remaining === 0;
                          return (
                            <motion.button key={key} type="button" whileTap={{ scale: 0.97 }} onClick={() => !done && handlePaymentTypeChange(key)}
                              style={{ padding: '14px', borderRadius: '10px', border: `1.5px solid ${active ? color : 'rgba(255,255,255,0.08)'}`, background: active ? `${color}10` : 'rgba(255,255,255,0.03)', cursor: done ? 'default' : 'pointer', textAlign: 'left', fontFamily: 'Poppins, sans-serif', opacity: done ? 0.5 : 1, position: 'relative' }}>
                              {done && <span style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '0.6rem', fontWeight: '800', color: '#34d399', background: 'rgba(52,211,153,0.15)', padding: '2px 6px', borderRadius: '999px' }}>DONE</span>}
                              <div style={{ fontSize: '0.72rem', fontWeight: '700', color: active ? color : 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>{pl}</div>
                              <div style={{ fontSize: '1rem', fontWeight: '900', color: active ? color : 'rgba(255,255,255,0.6)' }}>₹{total.toLocaleString('en-IN')}</div>
                              <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.35)', marginTop: '3px' }}>Paid ₹{paid.toLocaleString('en-IN')} · Left ₹{remaining.toLocaleString('en-IN')}</div>
                              <div style={{ height: '4px', borderRadius: '999px', background: 'rgba(255,255,255,0.08)', overflow: 'hidden', marginTop: '8px' }}>
                                <div style={{ height: '100%', borderRadius: '999px', background: done ? '#34d399' : color, width: `${Math.min(100, total > 0 ? (paid / total) * 100 : 0)}%`, transition: 'width 0.5s ease' }} />
                              </div>
                            </motion.button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Amount entry */}
                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <label style={lbl}>How Much Are You Paying Now? (₹)</label>
                      <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: '600' }}>Remaining: <span style={{ color: '#f87171' }}>₹{phaseRemaining.toLocaleString('en-IN')}</span></span>
                    </div>

                    {/* Quick presets */}
                    {phaseRemaining > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                        {presetAmounts.map(amt => (
                          <motion.button key={amt} type="button"
                            onClick={() => { setFormData(p => ({ ...p, paymentAmount: amt })); setPaymentConfirmed(false); if (errors.paymentAmount) setErrors(p => ({ ...p, paymentAmount: '' })); }}
                            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                            style={{ padding: '6px 14px', borderRadius: '8px', border: `1.5px solid ${formData.paymentAmount === amt ? '#F0B429' : 'rgba(255,255,255,0.1)'}`, background: formData.paymentAmount === amt ? 'rgba(240,180,41,0.12)' : 'rgba(255,255,255,0.04)', color: formData.paymentAmount === amt ? '#F0B429' : 'rgba(255,255,255,0.5)', fontSize: '0.78rem', fontWeight: '700', fontFamily: 'Poppins, sans-serif', cursor: 'pointer' }}>
                            {amt === phaseRemaining ? `₹${amt.toLocaleString('en-IN')} (Full)` : `₹${amt.toLocaleString('en-IN')}`}
                          </motion.button>
                        ))}
                      </div>
                    )}

                    <input type="number" name="paymentAmount" value={formData.paymentAmount || ''} onChange={handleInputChange} min="1" max={phaseRemaining}
                      placeholder={`Enter amount (1 – ${phaseRemaining.toLocaleString('en-IN')})`}
                      style={inpNoIcon(errors.paymentAmount)}
                      onFocus={e => { e.target.style.borderColor = '#F0B429'; }} onBlur={e => { e.target.style.borderColor = errors.paymentAmount ? '#f87171' : 'rgba(255,255,255,0.1)'; }} />
                    {errors.paymentAmount && <p style={errTxt}>⚠ {errors.paymentAmount}</p>}

                    {/* Live split preview */}
                    {formData.paymentAmount > 0 && formData.paymentAmount <= phaseRemaining && (
                      <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                        style={{ marginTop: '10px', padding: '12px 14px', borderRadius: '10px', background: isPartialPayment ? 'rgba(248,113,113,0.07)' : 'rgba(52,211,153,0.07)', border: `1px solid ${isPartialPayment ? 'rgba(248,113,113,0.25)' : 'rgba(52,211,153,0.25)'}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isPartialPayment ? '8px' : '0' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: isPartialPayment ? '#fca5a5' : '#34d399', fontSize: '0.78rem', fontWeight: '700' }}>
                            {isPartialPayment ? <AlertCircle size={13} /> : <Check size={13} />}
                            {isPartialPayment ? 'Partial Payment' : 'Full Phase Payment'}
                          </span>
                          <span style={{ color: 'white', fontWeight: '900', fontSize: '0.95rem' }}>₹{formData.paymentAmount.toLocaleString('en-IN')}</span>
                        </div>
                        {isPartialPayment && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem' }}>Balance still due after this</span>
                            <span style={{ color: '#f87171', fontWeight: '700', fontSize: '0.82rem' }}>₹{balanceAfterThis.toLocaleString('en-IN')}</span>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </div>

                  {/* Balance timeline */}
                  {isPartialPayment && (
                    <AnimatePresence>
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                        style={{ marginBottom: '18px', padding: '14px', borderRadius: '12px', background: 'rgba(248,113,113,0.07)', border: '1px solid rgba(248,113,113,0.25)' }}>
                        <label style={{ ...lbl, color: '#f87171', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                          <Clock size={13} /> When will you pay the remaining ₹{balanceAfterThis.toLocaleString('en-IN')}?
                        </label>
                        <div style={{ position: 'relative' }}>
                          <select name="balancePaymentTimeline" value={formData.balancePaymentTimeline} onChange={handleInputChange}
                            style={{ width: '100%', padding: '10px 32px 10px 12px', borderRadius: '8px', border: `1.5px solid ${errors.balancePaymentTimeline ? '#f87171' : 'rgba(248,113,113,0.3)'}`, background: '#1a1530', color: formData.balancePaymentTimeline ? 'white' : 'rgba(255,255,255,0.35)', fontSize: '0.86rem', fontFamily: 'Poppins, sans-serif', outline: 'none', cursor: 'pointer', appearance: 'none' as const, boxSizing: 'border-box' as const }}
                            onFocus={e => { e.target.style.borderColor = '#f87171'; }} onBlur={e => { e.target.style.borderColor = errors.balancePaymentTimeline ? '#f87171' : 'rgba(248,113,113,0.3)'; }}>
                            <option value="">Select a timeline</option>
                            <option value="Within 2 days">Within 2 days</option>
                            <option value="Within 5 days">Within 5 days</option>
                          
                          </select>
                          <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.35)', pointerEvents: 'none', fontSize: '0.7rem' }}>▾</span>
                        </div>
                        {errors.balancePaymentTimeline && <p style={errTxt}>⚠ {errors.balancePaymentTimeline}</p>}
                      </motion.div>
                    </AnimatePresence>
                  )}

                  {/* Payment note */}
                  <div style={{ marginBottom: '18px' }}>
                    <label style={lbl}>Payment Note (optional)</label>
                    <input type="text" name="paymentNote" value={formData.paymentNote} onChange={handleInputChange}
                      placeholder="e.g. Paying ₹500 now, rest in 5 days"
                      style={{ ...inpNoIcon(), color: 'rgba(255,255,255,0.6)' }}
                      onFocus={e => { e.target.style.borderColor = '#F0B429'; }} onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }} />
                  </div>

                  {/* QR + UPI */}
                  {formData.paymentAmount > 0 && (
                    <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        <div style={{ width: '26px', height: '26px', borderRadius: '7px', background: 'linear-gradient(135deg,#F0B429,#FF6535)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0d0b14', fontWeight: '900', fontSize: '0.8rem' }}>G</div>
                        <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: '600', fontSize: '0.84rem' }}>Pay via GPay / Any UPI App</span>
                      </div>
                      <div style={{ padding: '20px' }}>
                        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', marginBottom: '14px' }}>
                          Scan QR to pay <span style={{ color: '#F0B429', fontWeight: '900', fontSize: '1.2rem' }}>₹{formData.paymentAmount.toLocaleString('en-IN')}</span>
                          {isPartialPayment && <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.72rem' }}> (partial)</span>}
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                          <div style={{ padding: '10px', borderRadius: '14px', background: 'white', boxShadow: '0 4px 24px rgba(240,180,41,0.2)', border: '2px solid rgba(240,180,41,0.3)' }}>
                            <img src={qrCodeImage} alt="Payment QR Code" style={{ width: '160px', height: '160px', objectFit: 'contain', borderRadius: '6px', display: 'block' }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                          <span style={lbl}>Or Copy UPI ID</span>
                          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderRadius: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                          <div>
                            <p style={{ ...lbl, marginBottom: '2px' }}>UPI ID</p>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: '700', color: 'white' }}>9894449002@okbizaxis</span>
                          </div>
                          <motion.button type="button" onClick={copyUpiId} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                            style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.78rem', background: 'linear-gradient(135deg,#F0B429,#FF6535)', color: '#0d0b14' }}>Copy</motion.button>
                        </div>
                        {/* Bank Transfer */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '16px 0 10px' }}>
                          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                          <span style={lbl}>Or Bank Transfer</span>
                          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }} />
                        </div>
                        <div style={{ padding: '14px', borderRadius: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                          {[['Account Name', 'Vista Tech', false], ['Account Number', '24340200002436', true], ['IFSC Code', 'FDRL0002434', true]].map(([label, value, copyable]) => (
                            <div key={label as string} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                              <span style={{ ...lbl, margin: 0 }}>{label}</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontFamily: 'monospace', fontSize: '0.88rem', fontWeight: '700', color: 'white' }}>{value}</span>
                                {copyable && (
                                  <motion.button type="button" onClick={() => copyText(value as string, label as string)} whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                                    style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.7rem', background: 'linear-gradient(135deg,#F0B429,#FF6535)', color: '#0d0b14' }}>Copy</motion.button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Screenshot */}
                  {formData.paymentAmount > 0 && (
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}><Upload size={13} /> Upload Payment Screenshot</label>
                      <div style={{ borderRadius: '10px', border: `1.5px dashed ${errors.paymentScreenshot ? '#f87171' : formData.paymentScreenshot ? '#34d399' : 'rgba(255,255,255,0.15)'}`, background: errors.paymentScreenshot ? 'rgba(248,113,113,0.05)' : formData.paymentScreenshot ? 'rgba(52,211,153,0.05)' : 'rgba(255,255,255,0.02)' }}>
                        <input type="file" accept="image/*" onChange={handleFileChange} style={{ width: '100%', padding: '12px 16px', fontSize: '0.84rem', color: 'rgba(255,255,255,0.55)', cursor: 'pointer', background: 'transparent', outline: 'none', fontFamily: 'Poppins, sans-serif', boxSizing: 'border-box' as const }} />
                      </div>
                      {errors.paymentScreenshot && <p style={errTxt}>⚠ {errors.paymentScreenshot}</p>}
                      {formData.paymentScreenshot && <p style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#34d399', fontSize: '0.75rem', marginTop: '5px', fontWeight: '600' }}><Check size={13} /> {formData.paymentScreenshot.name}</p>}
                    </div>
                  )}

                  {/* Confirm payment */}
                  <motion.button type="button"
                    onClick={() => {
                      if (formData.paymentAmount <= 0) { setErrors(p => ({ ...p, paymentAmount: 'Enter the amount you are paying now' })); return; }
                      if (!formData.paymentScreenshot && formData.paymentAmount > 0) { setErrors(p => ({ ...p, paymentScreenshot: 'Please upload your payment screenshot first' })); return; }
                      setPaymentConfirmed(true);
                      if (errors.paymentConfirmed) setErrors(p => ({ ...p, paymentConfirmed: '' }));
                    }}
                    disabled={paymentConfirmed}
                    whileHover={paymentConfirmed ? {} : { scale: 1.01 }} whileTap={paymentConfirmed ? {} : { scale: 0.97 }}
                    style={{ width: '100%', padding: '13px', borderRadius: '10px', border: paymentConfirmed ? '1.5px solid rgba(52,211,153,0.4)' : 'none', cursor: paymentConfirmed ? 'default' : 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: paymentConfirmed ? 'rgba(52,211,153,0.12)' : 'linear-gradient(135deg,#10b981,#059669)', color: paymentConfirmed ? '#34d399' : 'white', boxShadow: paymentConfirmed ? 'none' : '0 6px 20px rgba(16,185,129,0.3)' }}>
                    <Check size={17} />
                    {paymentConfirmed ? `✓ ₹${formData.paymentAmount.toLocaleString('en-IN')} Payment Confirmed` : 'I Have Paid — Confirm Payment'}
                  </motion.button>
                  {errors.paymentConfirmed && <p style={{ ...errTxt, marginTop: '8px', textAlign: 'center' }}>⚠ {errors.paymentConfirmed}</p>}
                </div>
              )}

              {/* Final submit */}
              {paymentConfirmed && !paymentHistory?.isFullyPaid && (
                <motion.button type="button" onClick={handleSubmit} disabled={isSubmitting}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  whileHover={isSubmitting ? {} : { scale: 1.01 }} whileTap={isSubmitting ? {} : { scale: 0.97 }}
                  style={{ width: '100%', marginTop: '14px', padding: '14px', borderRadius: '12px', border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontFamily: 'Poppins, sans-serif', fontWeight: '700', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: isSubmitting ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg,#2b2240,#4e3a69)', color: isSubmitting ? 'rgba(255,255,255,0.3)' : 'white', boxShadow: isSubmitting ? 'none' : '0 8px 28px rgba(43,34,64,0.5)' }}>
                  {isSubmitting
                    ? <><span style={{ width: '18px', height: '18px', border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />Submitting...</>
                    : <><Check size={20} />Submit Registration & Payment</>}
                </motion.button>
              )}

            </motion.div>
          </AnimatePresence>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem' }}><span>🔒</span> SSL encrypted · Every instalment tracked automatically</p>
            <p style={{ color: 'rgba(255,255,255,0.12)', fontSize: '0.7rem', marginTop: '5px' }}>© Vista Tech Solutions · Secure Registration Portal</p>
          </div>
        </div>
      </div>

      {showTermsModal && <TermsModal />}
      <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
};

export default StudentRegistrationForm;